import { Copy } from '@styled-icons/feather/Copy';

import * as SS from './successStyles';

export type AccessPanelProps = {
  code: string;
  hasPassword: boolean;
  onCopy: () => void;
};

/**
 * What the member signs in with, on the Welcome step.
 *
 * A first-time member gets a generated access code, which doubles as their password.
 * A member who already set a password gets no code at all — the API declines to mint
 * one because doing so would overwrite the password they chose — so this branches on
 * `hasPassword` rather than on the code being empty. The distinction matters: an
 * existing member can still carry the code from their original signup, and showing
 * that as "your password" would be wrong.
 */
const AccessPanel = ({ code, hasPassword, onCopy }: AccessPanelProps) => {
  if (hasPassword) {
    return (
      <SS.CodeSection>
        <SS.Label>Your account</SS.Label>
        <SS.Panel>
          <SS.PanelLead>You already have a Savings Club account.</SS.PanelLead>
          <SS.PanelText>
            Sign in with the email and password you already use &mdash; we
            haven&apos;t changed them.
          </SS.PanelText>
          <SS.PanelText>
            Your new membership is already in the app: tap the membership chip
            at the top of the screen to switch between memberships.
          </SS.PanelText>
        </SS.Panel>
      </SS.CodeSection>
    );
  }

  const digits = (code || '----').split('');

  return (
    <SS.CodeSection>
      <SS.Label>Your access code</SS.Label>
      <SS.CodeRow>
        <SS.Caps>
          {digits.map((digit, index) => (
            <SS.Cap key={index}>{digit}</SS.Cap>
          ))}
        </SS.Caps>
        <SS.CopyBtn type="button" onClick={onCopy} aria-label="Copy access code">
          Copy <Copy />
        </SS.CopyBtn>
      </SS.CodeRow>
      <SS.CodeHint>This is your password. Keep it safe.</SS.CodeHint>
    </SS.CodeSection>
  );
};

export default AccessPanel;
